import { useLayoutEffect } from "react";
import { dehydrate, hydrate, useQueryClient } from "@tanstack/react-query";

const CACHE_KEY = "mg-query-cache";
const CACHE_MAX_AGE = 24 * 60 * 60 * 1000;
const CACHE_BUSTER = "v1";

interface PersistedQueryCache {
  timestamp: number;
  buster: string;
  state: ReturnType<typeof dehydrate>;
}

function isPersistedQueryCache(value: unknown): value is PersistedQueryCache {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as Partial<PersistedQueryCache>;
  return (
    typeof candidate.timestamp === "number" &&
    typeof candidate.buster === "string" &&
    typeof candidate.state === "object" &&
    candidate.state !== null
  );
}

/**
 * Restaure et sauvegarde le cache des requêtes dans localStorage.
 * Monté côté client uniquement (aucun rendu) : le contenu déjà consulté
 * s'affiche instantanément au retour sur le site.
 */
export function QueryPersistence() {
  const queryClient = useQueryClient();

  // useLayoutEffect : la restauration a lieu AVANT le premier affichage,
  // ce qui évite tout changement visuel tardif après le rendu initial.
  useLayoutEffect(() => {
    if (typeof window === "undefined") return;
    let saveTimer: ReturnType<typeof setTimeout> | undefined;

    try {
      const serializedCache = window.localStorage.getItem(CACHE_KEY);
      if (serializedCache) {
        const persisted: unknown = JSON.parse(serializedCache);
        if (
          isPersistedQueryCache(persisted) &&
          persisted.buster === CACHE_BUSTER &&
          Date.now() - persisted.timestamp <= CACHE_MAX_AGE
        ) {
          // On ne restaure que les requêtes absentes du cache : les données
          // fraîches du rendu serveur ne sont jamais écrasées par du cache local.
          const cache = queryClient.getQueryCache();
          const state = {
            ...persisted.state,
            queries: (persisted.state.queries ?? []).filter((query) => !cache.get(query.queryHash)),
          };
          hydrate(queryClient, state);
        } else {
          window.localStorage.removeItem(CACHE_KEY);
        }
      }
    } catch {
      // localStorage indisponible (mode privé, quota) : on ignore silencieusement.
    }

    const unsubscribe = queryClient.getQueryCache().subscribe(() => {
      if (saveTimer) clearTimeout(saveTimer);
      saveTimer = setTimeout(() => {
        try {
          const persisted: PersistedQueryCache = {
            timestamp: Date.now(),
            buster: CACHE_BUSTER,
            state: dehydrate(queryClient),
          };
          window.localStorage.setItem(CACHE_KEY, JSON.stringify(persisted));
        } catch {
          // Le cache mémoire continue de fonctionner si la persistance échoue.
        }
      }, 1000);
    });

    return () => {
      if (saveTimer) clearTimeout(saveTimer);
      unsubscribe();
    };
  }, [queryClient]);

  return null;
}
